import * as React from 'react';

import {
    BackButton,
    EventNavigation,
} from 'src/domains/layouts/webview/components/eventNavigation/CarouselSelection.style';
import { CarouselEventNavigation } from 'src/domains/layouts/webview/components/eventNavigation/CarouselEventNavigation';
import { EventsTableMobX } from 'src/domains/sportsbook/shared/Components';
import {
    getHeaderOptions,
    getDefaultHeaderOptions,
} from 'src/domains/layouts/webview/components/sports/general/config';
import { LayoutSkeleton } from 'src/domains/layouts/webview/modules/LayoutSkeleton';
import { observer } from 'src/utils/mobx-react';
import { SportEventsList } from 'src/domains/layouts/webview/components/sportEventsList/SportEventsList';
import { SportHeaderStar } from 'src/domains/layouts/webview/components/sportHeader/SportHeader';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { SectionWrapper, Wrapper } from './SportPage.style';

import { useAppStateContext } from 'src/appState/AppState';
import { SportRouteType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { useCommon } from 'src/domains/common/Common';
import { NewSportRacingContent } from 'src/domains/sportsbook/webview/components/racing/NewSportRacingContent';

interface SportPageGeneralPropsType {
    sportId: string;
    isAmericanFootball: boolean;
}

const specialCompetitionSlug = 'premier-league';

const SportPageGeneral = observer('SportPageGeneral', (props: SportPageGeneralPropsType): JSX.Element | null => {
    const { sportId, isAmericanFootball } = props;
    const optionItem = getHeaderOptions();
    const options = optionItem[sportId] ?? getDefaultHeaderOptions();

    if (sportId === 'football') {
        return (
            <section className='sport-general'>
                <SportHeaderStar
                    options={options}
                    sportId={sportId}
                    isCompetitionPage={false}
                    competitionId={null}
                />
                <EventsTableMobX
                    competitionId={null}
                    sportId={sportId}
                />
            </section>
        );
    } else {
        return (
            <section className='sport-general'>
                <SportHeaderStar
                    options={options}
                    sportId={sportId}
                    isCompetitionPage={false}
                    competitionId={null}
                />
                <SportEventsList
                    key='sport-events-list'
                    sport={sportId}
                    isAmericanFootball={isAmericanFootball}
                />
            </section>
        );
    }
});

const renderSportPageNavigation = (starRouter: StarRouter, currentView: SportRouteType): React.ReactNode => {
    const hideCompetitions = currentView.id === specialCompetitionSlug;

    if (hideCompetitions) {
        return null;
    }

    return (
        <EventNavigation>
            <BackButton
                onClick={starRouter.redirectToHomepage}
                position='left'
            />

            <CarouselEventNavigation currentView={currentView} />
        </EventNavigation>
    );
};

interface SportPagePropsType {
    currentView: SportRouteType;
    accountView: RightHandSideViewType | null;
    isSearchActive: boolean;
    isAmericanFootball: boolean;
}

export const SportPage = observer('SportPage', (props: SportPagePropsType) => {
    const common = useCommon();
    const { appSportsBookState } = useAppStateContext();
    const { currentView, accountView, isSearchActive, isAmericanFootball } = props;
    const starRouter = StarRouter.get(common);

    if (currentView.nameType === 'races') {
        return (
            <SectionWrapper>
                <LayoutSkeleton
                    currentView={currentView}
                    accountView={accountView}
                    isSearchActive={isSearchActive}
                    navigationType='global'
                >
                    <Wrapper>
                        <NewSportRacingContent
                            key={currentView.id}
                            sport={currentView.id}
                            eventId={appSportsBookState.models.id.getEventIdOption(currentView.event?.id ?? null)}
                            filterType={currentView.type}
                        />
                    </Wrapper>
                </LayoutSkeleton>
            </SectionWrapper>
        );
    }

    return (
        <SectionWrapper>
            <LayoutSkeleton
                currentView={currentView}
                accountView={accountView}
                isSearchActive={isSearchActive}
                navigationType='global'
            >
                <Wrapper>
                    {renderSportPageNavigation(starRouter, currentView)}
                    <SportPageGeneral
                        sportId={currentView.id}
                        isAmericanFootball={isAmericanFootball}
                    />
                    <SeoPagesContent pageSlug={currentView.id} />
                </Wrapper>
            </LayoutSkeleton>
        </SectionWrapper>
    );
});
